/**
 * 奇樹：ez-tree 一種底，葉色／花／果／光另掛。不混物種。
 */

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import type { PartRef, PlantForm } from '../content/types'
import {
  defaultPlantForm,
  FLOWER_KINDS,
  FRUIT_KINDS,
  TREE_BASES,
  isPlantSource,
} from '../content/partLibrary'
import { generateTreeFromPreset } from '../lib/ez-tree'
import type { PresetName } from '../lib/ez-tree'

const texLoader = new THREE.TextureLoader()
const leafTexCache = new Map<string, THREE.Texture>()
function leafTexture(kind: string) {
  const hit = leafTexCache.get(kind)
  if (hit) return hit
  const tex = texLoader.load(`./textures/tree/leaves/${kind}_color.png`)
  tex.colorSpace = THREE.SRGBColorSpace
  leafTexCache.set(kind, tex)
  return tex
}

const LEAF_TEX: Record<string, string> = {
  song: 'pine',
  tong: 'oak',
  liu: 'ash',
  zhu: 'aspen',
  guan: 'oak',
}

const geoCache = new Map<string, { branch: THREE.BufferGeometry; leaf: THREE.BufferGeometry }>()

function treeGeo(base: PlantForm['base'], seed: number) {
  const key = `${base}:${seed}`
  const hit = geoCache.get(key)
  if (hit) return hit
  const preset = (TREE_BASES.find(b => b.id === base)?.preset ?? 'pine_medium') as PresetName
  const { branchGeometry, leafGeometry } = generateTreeFromPreset(preset, seed + 17)
  const pack = { branch: branchGeometry, leaf: leafGeometry }
  geoCache.set(key, pack)
  return pack
}

function samplePoints(geo: THREE.BufferGeometry, count: number, seed: number) {
  const pos = geo.attributes.position
  if (!pos || pos.count === 0) return [] as THREE.Vector3[]
  const out: THREE.Vector3[] = []
  const step = Math.max(1, Math.floor(pos.count / count))
  for (let i = seed % step; i < pos.count && out.length < count; i += step) {
    const y = pos.getY(i)
    if (y < 4) continue
    out.push(new THREE.Vector3(pos.getX(i), y, pos.getZ(i)))
  }
  return out
}

function resolveForm(parts: PartRef[], form?: PlantForm): PlantForm {
  if (form) return { ...defaultPlantForm(), ...form }
  const trunk = parts.find(p => p.slot === 'trunk')?.source
  const base = trunk && TREE_BASES.some(b => b.id === trunk) ? trunk as PlantForm['base'] : 'song'
  return { ...defaultPlantForm(), base }
}

function hexColor(hex: string) {
  return new THREE.Color(hex)
}

export function PlantDoll({
  parts,
  form,
  scale = 0.17,
}: {
  parts: PartRef[]
  form?: PlantForm
  scale?: number
}) {
  const root = useRef<THREE.Group>(null)
  const glowRef = useRef<THREE.Group>(null)
  const resolved = resolveForm(parts, form)
  const { branch, leaf } = useMemo(() => treeGeo(resolved.base, resolved.seed), [resolved.base, resolved.seed])
  const flowerPts = useMemo(
    () => resolved.flower === 'none' ? [] : samplePoints(leaf, 16, resolved.seed),
    [leaf, resolved.flower, resolved.seed],
  )
  const fruitPts = useMemo(
    () => resolved.fruit === 'none' ? [] : samplePoints(leaf, 12, resolved.seed + 5),
    [leaf, resolved.fruit, resolved.seed],
  )

  const trunkMat = useMemo(() => new THREE.MeshStandardMaterial({
    color: '#3a3228',
    roughness: 0.96,
    metalness: 0,
    flatShading: true,
  }), [])

  const leafMat = useMemo(() => {
    const c = hexColor(resolved.leafTint)
    return new THREE.MeshStandardMaterial({
      color: c,
      map: leafTexture(LEAF_TEX[resolved.base] ?? 'oak'),
      roughness: 0.9,
      metalness: 0,
      side: THREE.DoubleSide,
      alphaTest: 0.08,
      emissive: resolved.glow ? c : new THREE.Color(0x000000),
      emissiveIntensity: resolved.glow ? 0.28 : 0,
    })
  }, [resolved.leafTint, resolved.base, resolved.glow])

  const flowerDef = FLOWER_KINDS.find(f => f.id === resolved.flower)
  const fruitDef = FRUIT_KINDS.find(f => f.id === resolved.fruit)
  const flowerMat = useMemo(() => new THREE.MeshStandardMaterial({
    color: flowerDef?.hex ?? '#a83a32',
    roughness: 0.85,
    metalness: 0,
    flatShading: true,
    emissive: hexColor(flowerDef?.hex ?? '#a83a32'),
    emissiveIntensity: resolved.glow ? 0.55 : 0.12,
  }), [flowerDef?.hex, resolved.glow])
  const fruitMat = useMemo(() => new THREE.MeshStandardMaterial({
    color: fruitDef?.hex ?? '#6a2a22',
    roughness: 0.7,
    metalness: resolved.fruit === 'jin' ? 0.35 : 0,
    flatShading: true,
    emissive: hexColor(fruitDef?.hex ?? '#6a2a22'),
    emissiveIntensity: resolved.glow ? 0.7 : resolved.fruit === 'jin' ? 0.25 : 0.08,
  }), [fruitDef?.hex, resolved.glow, resolved.fruit])

  useFrame(({ clock }) => {
    if (root.current) root.current.rotation.z = Math.sin(clock.elapsedTime * 0.9) * 0.02
    if (glowRef.current && resolved.glow) {
      const pulse = 0.55 + Math.sin(clock.elapsedTime * 2.2) * 0.2
      glowRef.current.traverse(obj => {
        const mesh = obj as THREE.Mesh
        const mat = mesh.material as THREE.MeshStandardMaterial
        if (mat?.emissiveIntensity !== undefined) mat.emissiveIntensity = pulse
      })
    }
  })

  return (
    <group ref={root} scale={scale}>
      <mesh geometry={branch} material={trunkMat} />
      {leaf.attributes.position && <mesh geometry={leaf} material={leafMat} />}
      <group ref={glowRef}>
        {flowerPts.map((p, i) => (
          <mesh key={`fl-${i}`} material={flowerMat} position={p}>
            {resolved.flower === 'he'
              ? <sphereGeometry args={[0.55, 8, 6]} />
              : <sphereGeometry args={[0.32, 7, 5]} />}
          </mesh>
        ))}
        {fruitPts.map((p, i) => (
          <mesh key={`fr-${i}`} material={fruitMat} position={p.clone().add(new THREE.Vector3(0.15, -0.4, 0))}>
            <sphereGeometry args={[resolved.fruit === 'jin' ? 0.38 : 0.28, 7, 5]} />
          </mesh>
        ))}
      </group>
    </group>
  )
}

export function isPlantSourceId(id: string) {
  return isPlantSource(id)
}
